import React, { memo, useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { createSelector } from 'reselect';
import KeepAlive, { useActivate, useUnactivate } from 'react-activation';

import { logging, logPostInfo } from '../../actions/log';
import { getLocalMachineInfo } from '../../actions/native';
import { downloadAddSoftList } from '../../actions/downloadTask';
import { cacheKeys } from '../../constants/storageKeys';
import {
  getRecoBanner,
  getRecoRecommend,
  getSoftInfos,
  getGuessLikes,
} from '../../actions/recommend';
import CardIndex from '../../card/CardIndex/CardIndex';
import { CONTEXT } from '../../config';
import { getQueryString, getParamString } from '../../utils/utils';
import GuessLikes from './GuessLikes';
import styles from './Recommend.module.less';
import Loading from '../../components/Loading/Loading';
import { logNative, getDefaultPage } from '../../utils/utils';
import { setUserFeedbackVisible } from '../../actions/layout';
import { setFeedbackTab } from '../../actions/userFeedback';

const selectRecoBanner = createSelector(
  state => state.recommend,
  recommend => recommend.recoBanner,
);
const selectRecoRecommend = createSelector(
  state => state.recommend,
  recommend => recommend.recoRecommend,
);

const selectSoftInfos = createSelector(
  state => state.recommend,
  recommend => recommend.softInfos,
);

const selectCommandLine = createSelector(
  state => state.native,
  native => native.commandLine,
);
const selectLocalMachineInfo = createSelector(
  state => state.native,
  native => native.machineInfo,
);

const selectLogInfo = createSelector(
  state => state.log,
  log => log.logInfo,
);

const selectUpgradeSofts = createSelector(
  state => state.native,
  native => native.upgradeSofts,
);

const selectSilentInstSoft = createSelector(
  state => state.native,
  native => native.supportSilentInstSoft,
);
const selectIsEnabledSilent = createSelector(
  state => state.layout,
  layout => layout.isEnabledSilent,
);
const selectSystemType = createSelector(
  state => state.layout,
  layout => layout.systemType,
);

let loggingRender = false;
function Recommend() {
  // export default memo(() => {
  const dispatch = useDispatch();
  const location = useLocation();
  const commandLine = useSelector(selectCommandLine);
  const history = useHistory();
  const machineInfo = useSelector(selectLocalMachineInfo);
  const upgradeSofts = useSelector(selectUpgradeSofts); // 可更新
  const silentInstSoft = useSelector(selectSilentInstSoft); // 支持快速静默安装的应用列表
  const isEnabledSilent = useSelector(selectIsEnabledSilent); // 是否打开快速静默安装
  const systemType = useSelector(selectSystemType); // 系统模式

  const recoBanner = useSelector(selectRecoBanner);
  const recoRecommend = useSelector(selectRecoRecommend);

  const softInfos = useSelector(selectSoftInfos);

  const logInfoData = useSelector(selectLogInfo);

  const [getBannerKey, setGetBannerKey] = useState(null);
  const [tagSource, setTagSource] = useState('Reco');
  const [initTime, setInitTime] = useState(+new Date());

  // 浏览时长打桩
  const leaveTimeLog = useCallback(() => {
    const leaveTime = +new Date();
    dispatch(
      logging({
        category: 'I',
        action: 'View_Time',
        label: '',
        param2: {
          source: tagSource || `Reco`,
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
          initTime,
          leaveTime,
          time: leaveTime - initTime,
        },
      }),
    );
  }, [tagSource, initTime]);

  const pagePvLog = useCallback(() => {
    // 当commandLine存在  跳入其他页面时，不打桩。除非是推荐页面
    if (commandLine && !skipOtherPage()) {
      dispatch(
        logging({
          category: 'P',
          action: 'Reco',
          label: '',
          param2: {
            source: 'Reco',
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
          },
        }),
      );
      dispatch(
        logPostInfo({
          source: 'Reco',
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
        }),
      );
    }
  }, [commandLine]);

  useActivate(() => {
    setInitTime(+new Date());
  });
  useUnactivate(() => {
    leaveTimeLog();
  });

  useEffect(() => {
    // 由于是第一个桩，几乎与注册同时，避免丢桩所以延时打桩
    setTimeout(() => {
      pagePvLog();
    }, 100);
  }, [commandLine]);
  useActivate(() => {
    pagePvLog();
  });

  useEffect(() => {
    if (!machineInfo) {
      dispatch(getLocalMachineInfo());
    }
  }, [machineInfo]);

  // 命令行进入调起反馈弹窗
  useEffect(() => {
    const boxTag = getQueryString('boxTag', location?.search);
    if (boxTag) {
      if (boxTag === 'question') {
        dispatch(setFeedbackTab('question'));
        dispatch(setUserFeedbackVisible(true));
      }
      if (boxTag === 'submit') {
        dispatch(setFeedbackTab('submit'));
        dispatch(setUserFeedbackVisible(true));
      }
      history.replace(`${CONTEXT}/recommend`);
    }
  }, [location]);

  // 判断是否为外部调起
  const skipOtherPage = () => {
    let skip = false;
    // 管家直接进入详情页 并且触发下载
    if (commandLine && commandLine.indexOf('-c pcm_security_install') > -1) {
      const softID = commandLine.split('#')[1] || '';
      const bizId = commandLine.split('#')[2] || '';
      if (softID || bizId) {
        skip = true;
      }
    }
    const params = [
      '-c pcm_toolbox_update',
      '-c pcm_toolbox_uninstall',
      '-c pcm_tanchuang',
      '-c pcm_clean_uninstall',
      '-c pcm_hidden_install',
    ];

    // 调起商店，并跳入某个页面
    if (commandLine && params.find(item => commandLine.indexOf(item) > -1)) {
      skip = true;
    }
    if (commandLine && commandLine.indexOf('-c open_page') > -1) {
      const page = getParamString('page', commandLine);
      const defaultPage = getDefaultPage();
      // 不为默认页面的时候，且目标页面也不是指向默认页面
      if (page !== '' && defaultPage.indexOf(page) === -1) {
        skip = true;
      }
    }
    return skip;
  };

  // 处理进入推荐页面就开始批量下载软件。
  useEffect(() => {
    const softIdsStr = getQueryString('softIds', location.search) || '';
    const pnsStr = getQueryString('pns', location.search) || '';
    const bizInfoStr = getQueryString('bizInfo', location.search) || '{}';
    if ((softIdsStr || pnsStr) && bizInfoStr) {
      const softIds = softIdsStr ? softIdsStr.split(',') : [];
      const pns = pnsStr ? pnsStr.split(',') : [];
      const bizInfo = JSON.parse(bizInfoStr);
      dispatch(getSoftInfos({ softIds, pns, bizInfo }));
    }
  }, [dispatch, commandLine, location.search]);

  /**
   * 进入页面就下载, 等待更新列表获取完成才开始下载
   */
  useEffect(() => {
    const softIdsStr = getQueryString('softIds', location.search) || '';
    const pnsStr = getQueryString('pns', location.search) || '';
    const bizInfoStr = getQueryString('bizInfo', location.search) || '{}';
    const recoIsDownload = sessionStorage.getItem('recoIsDownload');
    if (
      softInfos &&
      softInfos.data &&
      softInfos.data.length > 0 &&
      softInfos.softIds.join(',') === softIdsStr &&
      softInfos.pns.join(',') === pnsStr &&
      silentInstSoft &&
      silentInstSoft.length >= 0 &&
      upgradeSofts &&
      upgradeSofts.length != undefined &&
      location &&
      location.search &&
      (!recoIsDownload || recoIsDownload !== `${softIdsStr}&${pnsStr}`)
    ) {
      const androidSoftList = [];
      const pcSoftList = [];
      softInfos.data.forEach(softItem => {
        const upgrade = upgradeSofts.find(
          soft_ =>
            (soft_.androidType !== '1' &&
              (soft_.softID === softItem.softID || soft_.SoftID === softItem.softID)) ||
            (soft_.androidType === '1' && soft_.packageName === softItem.packageName),
        );
        if (softItem && softItem.androidType === '1') {
          const logParam2 = {
            source: 'Reco',
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
            softid: softItem.packageName,
            softName: softItem.softName,
            packageName: softItem.packageName,
            versionCode: softItem.versionCode,
            softType: '1',
            type: upgrade ? 'u' : 'd',
            url: softItem.downLoadUrl || softItem.downloadUrl,
          };
          androidSoftList.push({ ...softItem, logInfo: logParam2, bizInfo: bizInfoStr });
        } else {
          const installparams = {};
          const logParam2 = {
            source: 'Reco',
            source_p: logInfoData.source_p,
            source_pp: logInfoData.source_pp,
            softid: softItem.softID,
            softName: softItem.softName,
            softType: '0',
            type: upgrade ? 'u' : 'd',
            url: softItem.downLoadUrl || softItem.downloadUrl,
          };

          if (silentInstSoft && isEnabledSilent) {
            const silentSoft = silentInstSoft.find(soft_ => soft_.softID === softItem.softID);
            if (silentSoft && silentSoft.softID) {
              logParam2.type = upgrade ? 'xu' : 'xd';
              if (silentSoft.silentParamDic) {
                installparams.quiet = silentSoft.silentParamDic;
              }
              if (silentSoft.silentDirParamDic) {
                installparams.todir = silentSoft.silentDirParamDic;
              }
            }
          }
          pcSoftList.push({ ...softItem, logInfo: logParam2, installparams, bizInfo: bizInfoStr });
        }
      });
      if (androidSoftList.length > 0) {
        dispatch(downloadAddSoftList(androidSoftList));
      }
      if (pcSoftList.length > 0) {
        dispatch(downloadAddSoftList(pcSoftList));
      }
      sessionStorage.setItem('recoIsDownload', `${softIdsStr}&${pnsStr}`);
    }
  }, [dispatch, softInfos, logInfoData, upgradeSofts, silentInstSoft, location]);

  /**
   * 获取banner数据，每次进来都获取
   * 因为广告接入，计算统计接口请求，和展示次数矛盾。
   * 所以改为：每次展示banner，都重新请求一次数据
   */
  useEffect(() => {
    const getBannerKeyNum = +new Date();
    setGetBannerKey(getBannerKeyNum);
    dispatch(getRecoBanner({ getBannerKey: getBannerKeyNum, pageType: 'soft' }));
  }, [systemType]);

  // useEffect(() => {
  //   dispatch(getRecoRecommend({ pageType: 'soft' }));
  // }, [location]);
  // 改为页面激活的时候重新获取一次数据，而不是根据location变化来重新获取数据 2023-7-25 16:24:23
  useActivate(() => {
    dispatch(getRecoRecommend({ pageType: 'soft' }));
  });
  useEffect(() => {
    dispatch(getRecoRecommend({ pageType: 'soft' }));
  }, []);
  /**
   * 猜你喜欢
   */
  useEffect(() => {
    dispatch(getGuessLikes({ skip: 0, limit: 60 }));
  }, []);

  useEffect(() => {
    if (recoRecommend && recoRecommend.pageType === 'soft' && recoRecommend.data.length > 0) {
      let source = 'Reco';
      if (recoRecommend.data[0].computerType) {
        source = `Reco_${recoRecommend.data[0].computerType}`;
      }
      setTagSource(source);
    }
  }, [recoRecommend]);

  const changView = content => {
    dispatch(
      logging({
        category: 'I',
        action: 'Reco_Content_Show',
        label: '',
        param2: {
          source: 'Reco',
          source_p: logInfoData.source_p,
          source_pp: logInfoData.source_pp,
          ...content,
        },
      }),
    );
  };

  // const loggingRenderDomStart = useCallback(() => {
  //   let nowTime = +new Date();
  //   const startInfo = JSON.parse(localStorage.getItem(cacheKeys.START_INFO) || '{}') || {};
  //   // 开始渲染页面的时候打一个时间点的桩
  //   dispatch(
  //     logging({
  //       category: 'M',
  //       action: 'Pcstore_Home_Load',
  //       label: '',
  //       param2: {
  //         source: tagSource || `Reco`,
  //         source_p: logInfoData.source_p,
  //         source_pp: logInfoData.source_pp,
  //         nowTime: nowTime, // 现在时间点
  //         dataLoadTime: nowTime - window.PC_STORE_START_HTML_LOAD_END,
  //         SessionID: startInfo.SessionID,
  //         SessionIDH5: window.PC_STORE_START_HTML_LOAD_END, // 加载html时间点
  //       },
  //     }),
  //   );
  //   logNative(
  //     `启动优化测试，进入推荐页面，获取数据完成 ${+new Date() -
  //       window.PC_STORE_START_HTML_LOAD_END}`,
  //   );
  // }, []);

  // 数据获取完成，打一个桩，即页面展示出来所用的时长
  useEffect(() => {
    if (
      recoBanner &&
      recoBanner.pageType === 'soft' &&
      recoBanner.getBannerKey === getBannerKey &&
      recoBanner.data &&
      recoBanner.data.length > 0 &&
      recoRecommend &&
      recoRecommend.pageType === 'soft' &&
      recoRecommend.data.length > 0
    ) {
      if (!window.Page_Data_Load_Success) {
        dispatch(
          logging({
            category: 'R',
            action: 'PCSTORE_Data_Load_End',
            param2: {
              source: 'PCSTORE_Data_Load_End',
              homePage: 'Reco',
              PCStoreStartBegin: window.PC_STORE_START_BEGIN, // 点击启动商店时间点
              PCStoreHtmlLoadEnd: window.PC_STORE_START_HTML_LOAD_END, // html下载完成时间点
              PCStoreJsLoadEnd: window.PC_STORE_START_JS_LOAD_END, // JS css下载完成时间点
              PCStoreDataLoadEnd: +new Date(), // 页面数据下载完成时间点
              jsLoadTime: window.PC_STORE_START_JS_LOAD_END - window.PC_STORE_START_HTML_LOAD_END, // JS下载完成所用时间
              dataLoadTime: +new Date() - window.PC_STORE_START_JS_LOAD_END, // 加载数据完成所用时间
              startToJsLoadTime: window.PC_STORE_START_JS_LOAD_END - window.PC_STORE_START_BEGIN, // 从点击启动 到 JS下载完成所用时间
              startToDataLoadTime: +new Date() - window.PC_STORE_START_BEGIN, // 从点击启动 到 加载数据完成所用时间
            },
          }),
        );
      }
      window.Page_Data_Load_Success = true;
    }
  }, [recoBanner, recoRecommend]);

  if (
    !(
      recoBanner &&
      recoBanner.pageType === 'soft' &&
      recoBanner.getBannerKey === getBannerKey &&
      recoBanner.data &&
      recoBanner.data.length > 0 &&
      recoRecommend &&
      recoRecommend.pageType === 'soft' &&
      recoRecommend.data.length > 0
    )
  ) {
    return (
      <div className={styles.loading}>
        <Loading />
      </div>
    );
  } else {
    // if (!loggingRender) {
    //   loggingRender = true;
    //   loggingRenderDomStart();
    // }
  }

  return (
    <div className={styles.page} pagescroll="PCStore_Page_Scroll">
      <div className={styles.page_container}>
        {tagSource &&
          recoBanner &&
          recoBanner.pageType === 'soft' &&
          recoBanner.getBannerKey === getBannerKey &&
          recoBanner.data &&
          recoBanner.data.length > 0 &&
          recoRecommend &&
          recoRecommend.pageType === 'soft' &&
          recoRecommend.data.length > 0 && (
            <React.Fragment>
              <div className={styles.reco_banner}>
                {recoBanner.data.map((item, i) => (
                  <CardIndex data={{ ...item }} key={i} logInfo={{ tag: tagSource, changView }} />
                ))}
              </div>
              {recoRecommend.data.map((item, i) => (
                <CardIndex data={{ ...item }} key={i} logInfo={{ tag: tagSource, changView }} />
              ))}
            </React.Fragment>
          )}
        <GuessLikes logInfo={{ tag: tagSource, changView }} />
        {/* {welcome && welcomeData && welcomeData.needShow && !welcomeInit && welcomeLoadImg && (
          <Welcome />
        )} */}
      </div>
    </div>
  );
}
// });

export default () => (
  <KeepAlive name="recommend">
    <Recommend />
  </KeepAlive>
);
